import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Feather, Plus, MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import StorageBar from "../components/StorageBar";
import NewEntryModal from "../components/NewEntryModal";
import { stripHtml, highlight } from "../lib/htmlStrip";
import StreakCard from "../components/StreakCard";
import CelebrationModal from "../components/CelebrationModal";
import { shouldCelebrateToday } from "../lib/celebrate";

const MOODS = ["Calm", "Happy", "Reflective", "Anxious", "Grateful", "Restless"];

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [q, setQ] = useState("");
  const [moodFilter, setMoodFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");

  const loadEntries = async () => {
    const params = {};
    if (q) params.q = q;
    if (moodFilter) params.mood = moodFilter;
    if (tagFilter) params.tag = tagFilter;
    const list = await api.get("/entries", { params });
    setEntries(list.data);
  };

  const loadAll = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      await loadEntries();
      const t = await api.get("/entries/tags/all");
      setTags(t.data);
    } catch {
      navigate("/", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const qp = new URLSearchParams(window.location.search);
    if (qp.get("drive_connected")) {
      toast.success("Google Drive connected!");
      window.history.replaceState(null, "", "/dashboard");
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => loadEntries(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, moodFilter, tagFilter]);

  const onCreated = (e) => {
    setEntries((prev) => [e, ...prev]);
    setShowNew(false);
    navigate(`/entry/${e.entry_id}`);
  };

  const anyFilter = q || moodFilter || tagFilter;

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen paper-grain" data-testid="dashboard-page">
      <TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-12 fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Welcome back, {user?.name?.split(" ")[0]}
          </p>
          <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter mt-3">
            Your quiet <span className="italic text-accent">diary</span>
          </h1>
        </div>

        <StorageBar user={user} />

        <div className="mt-6">
          <StreakCard refreshKey={entries.length} />
        </div>

        <div className="flex items-center justify-between mt-14 mb-6">
          <h2 className="font-serif text-3xl">Timeline</h2>
          <Button
            data-testid="new-entry-btn"
            onClick={() => setShowNew(true)}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={18} weight="bold" className="mr-2" /> New entry
          </Button>
        </div>

        {/* Search + Filters */}
        <div className="mb-6 space-y-4" data-testid="filters-row">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles and content…"
              className="pl-11 pr-11 rounded-full border-border h-11 bg-card"
            />
            {q && (
              <button
                data-testid="clear-search"
                onClick={() => setQ("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear"
              >
                <XIcon size={14} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button
                key={m}
                data-testid={`filter-mood-${m.toLowerCase()}`}
                onClick={() => setMoodFilter(moodFilter === m ? "" : m)}
                className={`text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors ${
                  moodFilter === m
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:border-primary"
                }`}
              >
                {m}
              </button>
            ))}
            {tags.slice(0, 12).map((t) => (
              <button
                key={t.tag}
                data-testid={`filter-tag-${t.tag}`}
                onClick={() => setTagFilter(tagFilter === t.tag ? "" : t.tag)}
                className={`text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors ${
                  tagFilter === t.tag
                    ? "bg-accent text-accent-foreground border-accent"
                    : "border-border text-muted-foreground hover:border-accent hover:text-accent"
                }`}
              >
                #{t.tag} <span className="opacity-60">·{t.count}</span>
              </button>
            ))}
            {anyFilter && (
              <button
                data-testid="clear-filters"
                onClick={() => { setQ(""); setMoodFilter(""); setTagFilter(""); }}
                className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground"
              >
                Clear all ×
              </button>
            )}
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="border border-border rounded-lg p-12 text-center bg-card fade-up" data-testid="empty-timeline">
            <Feather size={40} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <p className="font-serif text-2xl">
              {anyFilter ? "Nothing matches." : "A blank page."}
            </p>
            <p className="text-sm text-muted-foreground mt-2 mb-6">
              {anyFilter ? "Try different keywords or clear filters." : "Nothing yet. What happened today?"}
            </p>
            {!anyFilter && (
              <Button
                data-testid="empty-new-entry-btn"
                onClick={() => setShowNew(true)}
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Write your first entry
              </Button>
            )}
          </div>
        ) : (
          <ul className="space-y-6" data-testid="entries-list">
            {entries.map((e, i) => {
              const d = new Date(e.entry_date);
              const preview = stripHtml(e.content).slice(0, 200);
              return (
                <li key={e.entry_id} className="fade-up" style={{ animationDelay: `${i * 0.04}s` }}>
                  <Link
                    to={`/entry/${e.entry_id}`}
                    data-testid={`entry-item-${e.entry_id}`}
                    className="grid grid-cols-[80px_1fr] gap-6 p-6 border border-border rounded-lg bg-card hover:border-primary transition-colors block"
                  >
                    <div>
                      <div className="font-serif text-5xl font-light leading-none">{d.getDate()}</div>
                      <div className="text-xs uppercase tracking-[0.2em] mt-2 text-muted-foreground">
                        {d.toLocaleString("en-US", { month: "short" })}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h3
                        className="font-serif text-2xl mb-1 truncate"
                        dangerouslySetInnerHTML={{ __html: highlight(e.title || "Untitled", q) }}
                      />
                      <p
                        className="text-sm text-muted-foreground line-clamp-2 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: highlight(preview || "—", q) }}
                      />
                      <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        {e.mood && <span>· {e.mood}</span>}
                        {e.tags?.map((t) => (
                          <span key={t} className="text-accent">#{t}</span>
                        ))}
                        {e.attachments?.length > 0 && (
                          <span>· {e.attachments.length} attachment{e.attachments.length > 1 ? "s" : ""}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {showNew && (
        <NewEntryModal onClose={() => setShowNew(false)} onCreated={onCreated} />
      )}
      <CelebrationModal
        open={celebrate}
        onClose={() => setCelebrate(false)}
        firstName={user?.name?.split(" ")[0] || "you"}
      />
    </div>
  );
}
